// adapted from Willy

import React from "react";
import i18n from "i18next";
import PropTypes from "prop-types";
import {
	AutoTable,
	Button,
	ButtonDropdown,
	CardRow,
	Date,
	DropdownItem,
	EmptyState,
	Icon,
	Modal,
	Status,
	Tooltip
} from "@weezevent/nacre";
import { useTranslation } from "react-i18next";
import Style from "./event-config-card.module.scss";
import { useEventConfig } from "api/api-hooks/event-config/use-get-event-config";
import Loading from "components/loading";
import { Link } from "react-router-dom";
import { InfoNumber } from "components/info-number/info-number";
import CircleSpinner from "../../../../components/generic/spinners";

function EventName({ event }) {
	return (
		<div className={Style["event-card-title"]}>
			<span className={Style["event-card-title-name"]}>
				{event?.event_config?.name || event?.name}
			</span>
			<span className={Style["event-card-title-id"]}>E {event?.id}</span>
		</div>
	);
}

EventName.propTypes = {
	event: PropTypes.object.isRequired
};

function EventDateTime({ event }) {
	// the timezone may be specified directly in an ISO 8601 string

	/*
		If start date == end date:
			display date only once, display start and end time aside
		If start date != end date:
			display start date and time first, and end date and time after

	 */

	const startDateTime = event?.event_config?.start_date
		? new Date(event?.event_config?.start_date)
		: null;
	const endDateTime = event?.event_config?.end_date
		? new Date(event?.event_config?.end_date)
		: null;

	const sameDay = (d1, d2) => {
		return (
			d1.getFullYear() === d2.getFullYear() &&
			d1.getMonth() === d2.getMonth() &&
			d1.getDate() === d2.getDate()
		);
	};

	const dateStringFromDateTime = datetime =>
		datetime?.toLocaleDateString(i18n.language, {
			dateStyle: "medium"
		});

	const timeStringFromDateTime = datetime =>
		datetime?.toLocaleTimeString(i18n.language, {
			timeStyle: "short"
		});

	if (startDateTime && endDateTime && sameDay(startDateTime, endDateTime)) {
		return (
			<>
				<div className={Style["event-card-info-line"]}>
					<span className={Style["icon-inline-start-container"]}>
						<Icon name="calendar" margin={false} />
					</span>
					{dateStringFromDateTime(startDateTime)}
				</div>
				<div className={Style["event-card-info-line"]}>
					<span className={Style["icon-inline-start-container"]}>
						<Icon name="time" margin={false} />
					</span>
					{timeStringFromDateTime(startDateTime)}
					<span className={Style["icon-inline-container"]}>
						<Icon
							name="arrow-right"
							margin={false}
							size={"small"}
						/>
					</span>
					{timeStringFromDateTime(endDateTime)}
				</div>
			</>
		);
	} else {
		return (
			<>
				<div className={Style["event-card-info-line"]}>
					<span className={Style["icon-inline-start-container"]}>
						<Icon name="calendar" margin={false} />
					</span>
					{dateStringFromDateTime(startDateTime)}
					{endDateTime && (
						<>
							<span className={Style["icon-inline-container"]}>
								<Icon
									name="arrow-right"
									margin={false}
									size={"small"}
								/>
							</span>
							{dateStringFromDateTime(endDateTime)}
						</>
					)}
				</div>
				<div className={Style["event-card-info-line"]}>
					<span className={Style["icon-inline-start-container"]}>
						<Icon name="time" margin={false} />
					</span>
					{timeStringFromDateTime(startDateTime)}
					{endDateTime && (
						<>
							<span className={Style["icon-inline-container"]}>
								<Icon
									name="arrow-right"
									margin={false}
									size={"small"}
								/>
							</span>
							{timeStringFromDateTime(endDateTime)}
						</>
					)}
				</div>
			</>
		);
	}
}

EventDateTime.propTypes = {
	event: PropTypes.object.isRequired
};

function EventParticipants({ event }) {
	const { t } = useTranslation();
	const attendeeCount = event?.stats?.attendee_count || 0;
	const translationKey =
		attendeeCount <= 1
			? "access.event.list.info.numberOfAttendeeSingular"
			: "access.event.list.info.numberOfAttendeePlural";

	return (
		<span className={Style["event-card-info-line-inline"]}>
			<span className={Style["icon-inline-start-container"]}>
				{/* non-semantic icon */}
				<Icon name="user" margin={false} color="access" />
			</span>
			<span className={Style["inline-number"]}>
				{t(translationKey, {
					count: attendeeCount
				})}
			</span>
		</span>
	);
}

EventParticipants.propTypes = {
	event: PropTypes.object.isRequired
};

function EventScans({ event }) {
	return (
		<span className={Style["event-card-info-line-inline"]}>
			<span className={Style["icon-inline-start-container"]}>
				<Icon name="scan" margin={false} color="access" />
			</span>
			<span className={Style["inline-number"]}>
				{event?.event_config?.scans || 0}
			</span>
		</span>
	);
}

EventScans.propTypes = {
	event: PropTypes.object.isRequired
};

function EventStatus({ event, onClick }) {
	const { t } = useTranslation();

	// WIP
	const status = event?.event_config?.status || "active";
	const color = "green";
	const text = t(`access.event.list.status.${status}`);
	const is_closed = event?.event_config?.is_closed || false;

	/*
	const items = [
		<DropdownItem key={2} item={t("access.event.list.status.archived")} />
	];

	<Status.Dropdown
					text={text}
					value={status}
					color={color}
					items={items}
				/>

	 */

	return !is_closed ? (
		<Status text={text} color={color} onClick={onClick} />
	) : (
		<Status
			text={t("eventList.status.closed")}
			color="red"
			onClick={onClick}
		/>
	);
}

EventStatus.propTypes = {
	event: PropTypes.object.isRequired,
	onClick: PropTypes.func
};

EventStatus.defaultProps = {
	onClick: () => {}
};

const QuickInspectModal = ({ tip, event, ...rest }) => {
	const [open, setOpen] = React.useState(false);

	const {
		data: eventConfig,
		isLoading: eventConfigIsLoading
	} = useEventConfig({
		eventId: event.id,
		params: {
			expand: "events"
		},
		options: {
			enabled: open
		}
	});

	const otherEvents = React.useMemo(() => eventConfig?.events || [], [
		eventConfig
	]);

	const { t } = useTranslation();

	const columns = [
		{
			key: "id",
			label: t(
				"access.event.list.quickInspectModal.secondaryEvents.columns.id"
			),
			render: event => event.id
		},
		{
			key: "name",
			label: t(
				"access.event.list.quickInspectModal.secondaryEvents.columns.name"
			),
			render: event => event.name
		}
	];

	return (
		<>
			<Modal
				allowExternalClosing={false}
				open={open}
				onClose={() => {
					if (open) setOpen(false);
				}}
				title={t("access.event.list.quickInspectModal.title", {
					name: event?.event_config?.name || event?.name
				})}
			>
				{eventConfigIsLoading ? (
					<div className={Style["modal-event-loader-container"]}>
						<Loading />
					</div>
				) : otherEvents.length === 0 ? (
					<EmptyState
						icon={<Icon name="list" color="access" />}
						title={t(
							"access.event.list.quickInspectModal.secondaryEvents.emptyState.title"
						)}
						subtitle={t(
							"access.event.list.quickInspectModal.secondaryEvents.emptyState.subtitle"
						)}
					/>
				) : (
					<AutoTable
						pk="id"
						useNew
						objects={otherEvents}
						columns={columns}
					/>
				)}
			</Modal>
			<Tooltip triggerOnHover title={tip}>
				<CardRow.IconButton
					as="button"
					onClick={() => {
						setOpen(!open);
					}}
					icon={<Icon name="list-ul" margin={false} />}
					{...rest}
				/>
			</Tooltip>
		</>
	);
};

QuickInspectModal.propTypes = {
	tip: PropTypes.node.isRequired,
	event: PropTypes.object.isRequired
};

export default function EventConfigCard({ event, organization, loadingStats }) {
	const { t } = useTranslation();

	const handleButtonClick = event => {
		event.preventDefault();
	};

	return (
		<Link
			to={`E${event?.id}/checkpoints`}
			className={Style["event-card-clickable"]}
		>
			<CardRow className={Style["event-card"]} as={"tr"}>
				<td className={[Style["event-card-inner-container"]]}>
					<div className={Style["event-card-title-container"]}>
						<EventName event={event} />
						<div
							className={Style["event-card-info-lines-container"]}
						>
							<EventDateTime event={event} />
							<span className={[Style["event-card-info-line"]]}>
								{loadingStats ? (
									<CircleSpinner />
								) : (
									<EventParticipants event={event} />
								)}
								{/*Only number of attendees is available from backend (end of July 2025)*/}
								{/*<EventScans event={event} />*/}
							</span>
						</div>
					</div>

					<div className={Style["event-card-infos-container"]}>
						<div className={Style["event-row-container"]}>
							<div
								className={Style["event-card-actions-buttons"]}
							>
								<div
									className={Style["event-card-big-numbers"]}
								>
									<InfoNumber
										text={t(
											"access.event.list.item.additionalEventNumberSubtitle",
											{
												count:
													event.event_config.events
														.length + 1
											}
										)}
										value={
											event.event_config.events.length + 1
										}
									/>
									<InfoNumber
										text={t(
											"access.event.list.item.checklistNumberSubtitle",
											{
												count:
													event.event_config
														.checkpoints.length
											}
										)}
										value={
											event.event_config.checkpoints
												.length
										}
									/>

									<InfoNumber
										text={t(
											"access.event.list.item.periodNumberSubtitle",
											{
												count:
													event.event_config.periods
														.length
											}
										)}
										value={
											event.event_config.periods.length
										}
									/>
								</div>

								<div className={Style["event-card-status"]}>
									<EventStatus event={event} />
								</div>

								<div
									className={Style["icon-button-container"]}
									onClick={e => e.preventDefault()}
								>
									<QuickInspectModal
										tip={t(
											"access.event.list.openModalQuickInspectButtonTip"
										)}
										event={event}
										organization={organization}
									/>
								</div>
								<ButtonDropdown
									size={"mini"}
									renderWhenClosed
									trigger={
										<Button
											inverted
											primary
											icon={
												<Icon name="chevron-bottom" />
											}
											label={t(
												"access.event.list.cta.actions"
											)}
											onClick={handleButtonClick}
										/>
									}
									items={[
										<div key="items">
											<div
												className={
													Style[
														"dropdown-button-items"
													]
												}
											>
												<DropdownItem
													as={"a"}
													item={t(
														"access.event.list.item.actionsdropdown.edit"
													)}
													href={`E${
														event?.id
													}/checkpoints`}
												/>
												{/*
											<Dropdown.Line />
											<DropdownItem
												item={t(
													"access.event.list.item.actionsdropdown.delete"
												)}
												onClick={() => {}}
											/>
											*/}
											</div>
										</div>
									]}
								/>
							</div>
						</div>
					</div>
				</td>
			</CardRow>
		</Link>
	);
}

EventConfigCard.propTypes = {
	event: PropTypes.object.isRequired,
	organization: PropTypes.object.isRequired,
	loadingStats: PropTypes.bool
};

EventConfigCard.defaultProps = {
	onChangeSelected: () => {},
	onChangeActionModal: () => {}
};
